import moment from 'moment';
import {
  getCampaignsOverviewData,
  getCustomersOverviewData,
  getCouponsOverviewData,
  getSpotCheckRecordsOverviewData,
} from '../services/AnalyticsApiHelper';
import { apiWithResponseHandle } from './LoadingUtil';
import { createAction } from '../utils';
import { parseCustomerChartData } from './DashboardModel';


export const parseRecalledRecordsChartData = (phototakingChart, barcodeChart) => { 
  // Return empty array if any chart is missing
  if (!phototakingChart || !barcodeChart) return [];

  return phototakingChart.periods.map((period, index) => ({
      // Get first date from period (e.g., "2025-02-04" from "2025-02-04 - 2025-02-04")
      name:  moment(period.split(' - ')[0]).format('D MMM'),
      phototaking: phototakingChart.points[index],
      barcode: barcodeChart.points[index]
  }));
};

const parseCampaignsApiData = (resp) => {
  const { analyticsData } = resp;
  return {
    totalPublishedCampaign: analyticsData.totalNumberOfPublishedCampaigns,
    totalCampaignConversion: analyticsData.totalNumberOfCampaignConversions,
    totalCampaignViews: analyticsData.totalNumberOfCampaignViews,
    couponCampaignConversionRate: analyticsData.couponCampaignConversionRate,
    earningCampaignConversionRate: analyticsData.earningCampaignConversionRate,
    campaignShareRate: analyticsData.campaignShareRate,

    totalPointsEarned: analyticsData.totalPointsEarned,
    totalPointsUsed: analyticsData.totalPointsUsed,

    totalCouponActivities: analyticsData.totalNumberOfCouponActivities,
    totalCouponRedeemByPoints:
      analyticsData.totalNumberOfCouponsRedeemedByPoints,
    totalCouponUsed: analyticsData.totalNumberOfCouponsUsed,
    totalCouponAcquiredByCampaigns:
      analyticsData.totalNumberOfCouponsAcquiredByCampaigns,
  };
};

const parseCustomersApiData = (resp) => {
  const { analyticsData } = resp;
  return {
    totalCustomers: {
      ...analyticsData.totalNumberOfCustomers,
    },
    activeCustomers: {
      ...analyticsData.totalNumberOfActiveCustomers,
    },
    newCustomers: {
      ...analyticsData.totalNumberOfNewCustomers,
    },
    nonRegistedCustomers: {
      ...analyticsData.totalNumberOfGuestCustomers,
    },
    chartData: parseCustomerChartData(
      analyticsData.totalNumberOfNewCustomersChart,
    ),
  };
};

const parseCouponsApiData = (resp) => {
  const { analyticsData } = resp;
  return {
    totalCouponActivities: analyticsData.totalNumberOfCouponActivities,
    totalCouponRedeemByPoints:
      analyticsData.totalNumberOfCouponsRedeemedByPoints,
    totalCouponUsed: analyticsData.totalNumberOfCouponsUsed,
    totalCouponAcquiredByCampaigns:
      analyticsData.totalNumberOfCouponsAcquiredByCampaigns,
  };
};

const parseSpotCheckRecordsApiData = (resp) => {
  const { analyticsData } = resp;
  return {
    totalNumberOfAutoApprovedTransactions: analyticsData.totalNumberOfAutoApprovedTransactions,
    percentageOfAutoApprovedTransactions:
      analyticsData.percentageOfAutoApprovedTransactions,
    totalNumberOfTransactions: analyticsData.totalNumberOfTransactions,
    totalNumberOfApprovedByEarningRuleTransactions:
      analyticsData.totalNumberOfApprovedByEarningRuleTransactions,
    totalNumberOfApprovedByAiTransactions:
      analyticsData.totalNumberOfApprovedByAiTransactions,
    totalNumberOfSpotCheckedTransactions:
      analyticsData.totalNumberOfSpotCheckedTransactions,
    totalNumberOfSpotCheckedPassedTransactions:
      analyticsData.totalNumberOfSpotCheckedPassedTransactions,
    totalNumberOfSpotCheckedRecalledTransactions:
      analyticsData.totalNumberOfSpotCheckedRecalledTransactions,
    chartData: parseRecalledRecordsChartData(
        analyticsData.totalNumberOfRecalledTransactionsPhototakingChart,
        analyticsData.totalNumberOfRecalledTransactionsBarcodeChart,
    )
  };
};

export default {
  namespace: 'overview',
  state: {
    customers: {},
    coupons: {},
    campaigns: {},
    spotCheckRecords: {},
  },
  reducers: {
    updateState(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    clearState(state, { payload }) {
      return {
        customers: {},
        coupons: {},
        campaigns: {},
        spotCheckRecords: {},
      };
    },
  },
  effects: {
    getCampaignsData: [
      function* ({ payload }, { put }) {
        const { startDate, endDate } = payload;
        const serviceArgs = [getCampaignsOverviewData, startDate, endDate];
        function* onSuccess(data) {
          const processedData = parseCampaignsApiData(data);
          yield put(createAction('updateState')({ campaigns: processedData }));
        }
        function* onFailed(data) {
          console.log('@Get campaigns overview data failed: ', data);
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
    getCustomersData: [
      function* ({ payload }, { put }) {
        const { startDate, endDate } = payload;
        const serviceArgs = [getCustomersOverviewData, startDate, endDate];
        function* onSuccess(data) {
          const processedData = parseCustomersApiData(data);
          yield put(createAction('updateState')({ customers: processedData }));
        }
        function* onFailed(data) {
          console.log('@Get customers overview data failed: ', data);
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
    getCouponsData: [
      function* ({ payload }, { put }) {
        const { startDate, endDate } = payload;
        const serviceArgs = [getCouponsOverviewData, startDate, endDate];
        function* onSuccess(data) {
          const processedData = parseCouponsApiData(data);
          yield put(createAction('updateState')({ coupons: processedData }));
        }
        function* onFailed(data) {
          console.log('@Get coupons overview data failed: ', data);
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],

    getSpotCheckRecordsData: [
      function* ({ payload }, { put }) {
        const { startDate, endDate } = payload;
        const serviceArgs = [getSpotCheckRecordsOverviewData, startDate, endDate];
        function* onSuccess(data) {
          const processedData = parseSpotCheckRecordsApiData(data);
          yield put(createAction('updateState')({ spotCheckRecords: processedData }));
        }
        function* onFailed(data) {
          console.log('@Get coupons overview data failed: ', data);
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ]
  },
};
